import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Stack,
  Container,
  Card,
  CardContent,
  MenuItem,
  InputAdornment,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  AccountBalance as BankIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getBase64Image = (imgUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL('image/png');
      resolve(dataURL);
    };
    img.onerror = reject;
    img.src = imgUrl;
  });
};

const ProFormaInvoice = () => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [contractors, setContractors] = useState([]);
  const [companyLogoBase64, setCompanyLogoBase64] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editInvoiceId, setEditInvoiceId] = useState(null);
  const editInvoiceData = location.state?.invoiceToEdit;

  // Vendor Payout States
  const [allContractors, setAllContractors] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [billingType, setBillingType] = useState('direct'); // 'direct' or 'vendor'
  const [selectedVendorId, setSelectedVendorId] = useState('');

  // Fetch contractors and vendors on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        // 1. Fetch contractor employees
        const empResponse = await axios.get(`${API_BASE_URL}/api/empcontract`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (empResponse.data && empResponse.data.success) {
          setAllContractors(empResponse.data.data);
          // Keep contractors state for direct contractors
          const directContractors = empResponse.data.data.filter(emp => emp.isDirectContractor === true);
          setContractors(directContractors);
        }

        // 2. Fetch vendor contracts (agencies)
        const vendorResponse = await axios.get(`${API_BASE_URL}/api/contract`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (vendorResponse.data && vendorResponse.data.success) {
          setVendors(vendorResponse.data.data);
        }
      } catch (error) {
        //console.error("Error fetching contractors/vendors:", error);
      }
    };

    fetchData();
  }, []);

  // Fetch company details on mount
  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await axios.get(`${API_BASE_URL}/api/cmp/current`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.data && response.data.success && response.data.data) {
          const company = response.data.data;
          setFormData((prev) => ({
            ...prev,
            billToCompany: company.companyname || prev.billToCompany,
            billToStreet: company.address || prev.billToStreet,
            billToCity: company.hq || prev.billToCity,
            billToPhone: company.contactNumber || prev.billToPhone,
            billToEmail: company.contactemail || prev.billToEmail,
            billToGst: company.GST || prev.billToGst,
          }));

          if (company.companyLogo) {
            const logoUrl = company.companyLogo.startsWith('http') ? company.companyLogo : `${API_BASE_URL}/uploads/${company.companyLogo}`;
            try {
              const base64 = await getBase64Image(logoUrl);
              setCompanyLogoBase64(base64);
            } catch (err) {
              //console.error("Error preloading company logo:", err);
            }
          }
        }
      } catch (error) {
        //console.error("Error fetching company details:", error);
      }
    };

    fetchCompanyDetails();
  }, []);

  const [formData, setFormData] = useState({
    // Header Info
    invoiceNumber: `PFI-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
    date: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 15 days due
    currency: 'INR',

    // Bill To (Client Company - Hardcoded/Defaulted to your company)
    billToCompany: 'PulseHR Solutions Pvt Ltd',
    billToStreet: '45 Tech Park, Phase 2, Sector 5',
    billToCity: 'Bangalore, Karnataka, 560001',
    billToPhone: '+91 80 4928 2382',
    billToEmail: 'finance@pulsehr.com',
    billToGst: '29AAAAA1111A1Z1',

    // Bill From (Contractor - Auto-filled upon selection)
    billFromName: '',
    billFromStreet: '',
    billFromCity: '',
    billFromPhone: '',
    billFromEmail: '',
    billFromGst: '', // PAN or GST of contractor

    // Bank / Payout Details (Auto-filled upon selection)
    bankName: '',
    accountName: '',
    accountNumber: '',
    ifscCode: '',

    // Selected Contract Employee details
    selectedEmployee: '',
    employeeName: '',
    employeeDesignation: '',
    contractPeriod: '',
    hourlyRate: '',
    totalHours: '0',
    contractTotal: '0',

    // Financials & Taxes
    gstRate: '18', // Standard 18% GST
    tdsRate: '10', // Standard 10% TDS
    otherChargesLabel: 'Technology Fee',
    otherChargesValue: '0',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editInvoiceData) {
      setIsEditMode(true);
      setEditInvoiceId(editInvoiceData.invoice_id);
      
      // Auto-detect billing type on edit
      const isVendor = editInvoiceData.invoiceNumber?.startsWith('VEND-');
      setBillingType(isVendor ? 'vendor' : 'direct');
      if (isVendor) {
        setSelectedVendorId(editInvoiceData.contractEmployee?.companyId || '');
      }
      
      // Split contractor address if available
      const addressVal = editInvoiceData.contractEmployee?.address || '';
      let street = addressVal;
      let cityStatePin = 'N/A';
      
      if (addressVal) {
        const parts = addressVal.split(',');
        if (parts.length >= 3) {
          cityStatePin = parts.slice(-3).join(',').trim();
          street = parts.slice(0, -3).join(',').trim();
        } else if (parts.length === 2) {
          cityStatePin = parts[1].trim();
          street = parts[0].trim();
        }
      } else {
        street = editInvoiceData.contractEmployee?.department || 'N/A';
      }

      setFormData({
        invoiceNumber: editInvoiceData.invoiceNumber,
        date: editInvoiceData.invoiceDate ? editInvoiceData.invoiceDate.split('T')[0] : '',
        dueDate: editInvoiceData.dueDate ? editInvoiceData.dueDate.split('T')[0] : '',
        currency: 'INR',

        // Bill To
        billToCompany: editInvoiceData.billToCompany || 'PulseHR Solutions Pvt Ltd',
        billToStreet: editInvoiceData.billToStreet || '45 Tech Park, Phase 2, Sector 5',
        billToCity: editInvoiceData.billToCity || 'Bangalore, Karnataka, 560001',
        billToPhone: editInvoiceData.billToPhone || '+91 80 4928 2382',
        billToEmail: editInvoiceData.billToEmail || 'finance@pulsehr.com',
        billToGst: editInvoiceData.billToGst || '29AAAAA1111A1Z1',

        // Bill From
        billFromName: editInvoiceData.contractEmployee?.fullName || '',
        billFromStreet: street,
        billFromCity: cityStatePin,
        billFromPhone: editInvoiceData.contractEmployee?.phoneNumber || '',
        billFromEmail: editInvoiceData.contractEmployee?.email || '',
        billFromGst: editInvoiceData.contractEmployee?.panNumber || '',

        // Bank Details
        bankName: editInvoiceData.contractEmployee?.bankName || '',
        accountName: editInvoiceData.contractEmployee?.accountName || '',
        accountNumber: editInvoiceData.contractEmployee?.accountNumber || '',
        ifscCode: editInvoiceData.contractEmployee?.ifscCode || '',

        // Selected Employee Details
        selectedEmployee: editInvoiceData.contract_employee_id,
        employeeName: editInvoiceData.contractEmployee?.fullName || '',
        employeeDesignation: editInvoiceData.contractEmployee?.designation || '',
        contractPeriod: editInvoiceData.contractEmployee?.contractPeriod || 'N/A',
        hourlyRate: editInvoiceData.hourlyRate ? editInvoiceData.hourlyRate.toString() : '0',
        totalHours: editInvoiceData.totalHours || '0',
        contractTotal: editInvoiceData.subtotal ? editInvoiceData.subtotal.toString() : '0',

        // Financials & Taxes
        gstRate: editInvoiceData.subtotal > 0 ? ((editInvoiceData.gstAmount / editInvoiceData.subtotal) * 100).toFixed(0) : '18',
        tdsRate: editInvoiceData.subtotal > 0 ? ((editInvoiceData.tdsAmount / editInvoiceData.subtotal) * 100).toFixed(0) : '10',
        otherChargesLabel: 'Technology Fee',
        otherChargesValue: '0',
      });
    }
  }, [editInvoiceData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      
      if (name === 'hourlyRate' || name === 'totalHours') {
        const rate = parseFloat(name === 'hourlyRate' ? value : prev.hourlyRate) || 0;
        const hours = parseFloat(name === 'totalHours' ? value : prev.totalHours) || 0;
        updated.contractTotal = (rate * hours).toFixed(2);
      }
      
      return updated;
    });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleEmployeeSelect = (e) => {
    const employeeId = e.target.value;
    const employee = contractors.find(emp => emp.c_employee_id === employeeId);
    if (employee) {
      // Split contractor address if available
      const addressVal = employee.address || '';
      let street = addressVal;
      let cityStatePin = 'N/A';
      
      if (addressVal) {
        const parts = addressVal.split(',');
        if (parts.length >= 3) {
          cityStatePin = parts.slice(-3).join(',').trim();
          street = parts.slice(0, -3).join(',').trim();
        } else if (parts.length === 2) {
          cityStatePin = parts[1].trim();
          street = parts[0].trim();
        }
      } else {
        // Fallback to department if address is empty
        street = employee.department || 'N/A';
      }

      const approvedHrs = (employee.approvedHours !== undefined && employee.approvedHours !== null) ? Number(employee.approvedHours) : 160;

      setFormData((prev) => ({
        ...prev,
        selectedEmployee: employeeId,
        employeeName: employee.fullName,
        employeeDesignation: employee.designation,
        contractPeriod: employee.contractPeriod || 'N/A',
        hourlyRate: employee.hourlyRate ? employee.hourlyRate.toString() : '0',
        totalHours: approvedHrs,
        contractTotal: ((employee.hourlyRate || 0) * approvedHrs).toFixed(2),
        
        // Auto-fill Bill From (Contractor Profile)
        billFromName: employee.fullName,
        billFromStreet: street,
        billFromCity: cityStatePin,
        billFromPhone: employee.phoneNumber || '',
        billFromEmail: employee.email || '',
        billFromGst: employee.panNumber || '',

        // Auto-fill Bank details
        bankName: employee.bankName || '',
        accountName: employee.accountName || '',
        accountNumber: employee.accountNumber || '',
        ifscCode: employee.ifscCode || ''
      }));
    }
  };

  const handleBillingTypeChange = (type) => {
    setBillingType(type);
    setSelectedVendorId('');
    clearForm();
    
    // Generate new default invoice number with appropriate prefix
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    const year = new Date().getFullYear();
    const prefix = type === 'vendor' ? 'VEND-PFI' : 'PFI';
    
    setFormData(prev => ({
      ...prev,
      invoiceNumber: `${prefix}-${year}-${randomNum}`,
      tdsRate: type === 'vendor' ? '2' : '10', // 2% TDS under 194C for vendors, 10% under 194J for direct contractors
    }));
  };

  const handleVendorSelect = (e) => {
    const vendorId = Number(e.target.value);
    setSelectedVendorId(vendorId);
    
    const vendor = vendors.find(v => v.id === vendorId);
    if (vendor) {
      // Find all contractors assigned to this vendor
      const assignedContractors = allContractors.filter(emp => emp.companyId === vendorId);
      
      if (assignedContractors.length === 0) {
        setSnackbar({
          open: true,
          message: `Warning: No contractors are currently assigned to ${vendor.companyName}. Payout will default to 0.`,
          severity: 'warning'
        });
      }
      
      // Load billing and contact details directly from the vendor contract table
      const vHourlyRate = vendor.hourlyRate ? vendor.hourlyRate.toString() : '0';
      const vApprovedHours = vendor.approvedHours ? vendor.approvedHours.toString() : '160';
      const vContractTotal = (parseFloat(vHourlyRate) * parseInt(vApprovedHours)).toFixed(2);

      const firstContractor = assignedContractors.length > 0 ? assignedContractors[0] : null;
      const representativeEmployeeId = firstContractor ? firstContractor.c_employee_id : 'NO_ASSIGNED_CONTRACTORS';

      setFormData((prev) => ({
        ...prev,
        // Selected Representative Details for DB storage
        selectedEmployee: representativeEmployeeId,
        employeeName: vendor.companyName,
        employeeDesignation: 'Consolidated Agency Payout',
        contractPeriod: `Contract Period: ${vendor.contractStartDate ? new Date(vendor.contractStartDate).toLocaleDateString() : 'N/A'} - ${vendor.contractEndDate ? new Date(vendor.contractEndDate).toLocaleDateString() : 'N/A'}`,
        hourlyRate: vHourlyRate,
        totalHours: vApprovedHours,
        contractTotal: vContractTotal,
        
        // Auto-fill Bill From (Vendor Agency Corporate Details directly from contracts table)
        billFromName: vendor.companyName,
        billFromStreet: vendor.companyAddress || 'N/A',
        billFromCity: 'Corporate Headquarters',
        billFromPhone: vendor.companyPhone || '',
        billFromEmail: vendor.companyEmail || '',
        billFromGst: vendor.companyPAN || 'N/A',
        
        // Auto-fill Corporate Bank details
        bankName: vendor.bankName || '',
        accountName: vendor.companyName || '',
        accountNumber: vendor.accountNumber || '',
        ifscCode: vendor.ifscCode || '',
        
        // Set Vendor specific TDS rate (2% under Section 194C)
        tdsRate: '2'
      }));
    }
  };

  const calculateSubtotal = () => {
    return parseFloat(formData.contractTotal) || 0;
  };

  const calculateGst = () => {
    const subtotal = calculateSubtotal();
    const rate = parseFloat(formData.gstRate) || 0;
    return subtotal * (rate / 100);
  };

  const calculateTds = () => {
    const subtotal = calculateSubtotal();
    const rate = parseFloat(formData.tdsRate) || 0;
    return subtotal * (rate / 100);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const gst = calculateGst();
    const tds = calculateTds();
    const other = parseFloat(formData.otherChargesValue) || 0;
    return subtotal + gst - tds + other;
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      'invoiceNumber', 'date', 'dueDate',
      'billFromName', 'billFromStreet',
      'selectedEmployee', 'bankName', 'accountName', 'accountNumber', 'ifscCode'
    ];

    requiredFields.forEach(field => {
      if (!formData[field] || String(formData[field]).trim() === '') {
        newErrors[field] = 'This field is required';
      }
    });

    if (billingType === 'vendor' && formData.selectedEmployee === 'NO_ASSIGNED_CONTRACTORS') {
      newErrors.selectedEmployee = 'Please assign at least one contractor to this vendor company first.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setSnackbar({
            open: true,
            message: 'No authentication token found. Please log in.',
            severity: 'error'
          });
          setLoading(false);
          return;
        }

        const subtotal = calculateSubtotal();
        const gstAmount = calculateGst();
        const tdsAmount = calculateTds();
        const netPayable = calculateTotal();

        // 1. Generate the PDF first and capture its base64 data URI
        const pdfDoc = generateInvoicePDF(false);
        const pdfBase64 = pdfDoc.output('datauristring');

        const requestUrl = isEditMode
          ? `${API_BASE_URL}/api/proforma-invoice/${editInvoiceId}`
          : `${API_BASE_URL}/api/proforma-invoice`;

        const requestMethod = isEditMode ? 'put' : 'post';

        const response = await axios({
          method: requestMethod,
          url: requestUrl,
          data: {
            contract_employee_id: formData.selectedEmployee,
            invoiceNumber: formData.invoiceNumber,
            invoiceDate: formData.date,
            dueDate: formData.dueDate,
            totalHours: Number(formData.totalHours),
            hourlyRate: Number(formData.hourlyRate),
            subtotal: Number(subtotal),
            gstAmount: Number(gstAmount),
            tdsAmount: Number(tdsAmount),
            netPayable: Number(netPayable),
            pdfFile: pdfBase64
          },
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.data && response.data.success) {
          setSnackbar({
            open: true,
            message: response.data.message || 'Pro Forma Invoice saved and emailed successfully!',
            severity: 'success'
          });
          pdfDoc.save(`proforma_${formData.invoiceNumber}.pdf`);
          setTimeout(() => {
            navigate('/proforma-history');
          }, 1500);
        } else {
          throw new Error(response.data.message || 'Failed to save the invoice.');
        }
      } catch (error) {
        //console.error("Error saving pro forma invoice:", error);
        setSnackbar({
          open: true,
          message: error.response?.data?.message || error.message || 'Failed to save invoice. Please try again.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    } else {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields to generate invoice',
        severity: 'error'
      });
    }
  };

  const generateInvoicePDF = (shouldSave = true, customData = null) => {
    const doc = new jsPDF();
    const data = customData || formData;
    const currencySym = data.currency === 'INR' ? 'Rs.' : '$';

    // 1. Draw Company Logo (Matches Payslip - Small and Elegant)
    if (companyLogoBase64) {
      try {
        doc.addImage(companyLogoBase64, 'PNG', 14, 9, 24, 10);
      } catch (err) {
        //console.error("Error drawing logo in PDF:", err);
      }
    }

    // 2. Centered Company Info (Matches Payslip)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(data.billToCompany, 105, 14, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    const addressText = `${data.billToStreet}, ${data.billToCity}`;
    const splitAddress = doc.splitTextToSize(addressText, 110);
    doc.text(splitAddress, 105, 19, { align: 'center' });

    // 3. Top Divider Line (Matches Payslip)
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.4);
    doc.line(14, 29, 196, 29);

    // 4. Centered Title (Matches Payslip Layout)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(26, 35, 126); // Brand primary color
    doc.text('PRO FORMA INVOICE', 105, 35, { align: 'center' });

    // 5. Bottom Divider Line (Matches Payslip)
    doc.line(14, 39, 196, 39);

    // 6. Two-Column Details Section (Matches Payslip Grid)
    doc.setTextColor(50, 50, 50);
    doc.setFontSize(9);
    
    // Left Column: Invoice Metadata
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE DETAILS:', 14, 46);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice No:`, 14, 52);
    doc.setFont('helvetica', 'bold');
    doc.text(data.invoiceNumber, 40, 52);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice Date:`, 14, 57);
    doc.text(data.date, 40, 57);
    
    doc.text(`Due Date:`, 14, 62);
    doc.text(data.dueDate, 40, 62);

    // Right Column: Contract details
    doc.setFont('helvetica', 'bold');
    doc.text('CONTRACT DETAILS:', 110, 46);
    doc.setFont('helvetica', 'normal');
    doc.text(`Consultant:`, 110, 52);
    doc.setFont('helvetica', 'bold');
    doc.text(data.employeeName, 138, 52);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Designation:`, 110, 57);
    doc.text(data.employeeDesignation, 138, 57);
    
    doc.text(`Period / Mode:`, 110, 62);
    doc.text(`${data.contractPeriod} / Hourly`, 138, 62);

    // Divider Line
    doc.setDrawColor(220, 220, 220);
    doc.line(14, 68, 196, 68);

    // 7. Two-Column Billing Parties (Bill From & Bill To)
    // Left Column: Service Provider (Bill From)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(26, 35, 126);
    doc.text('SERVICE PROVIDER (BILL FROM):', 14, 75);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(50, 50, 50);
    doc.text(data.billFromName, 14, 81);
    doc.text(data.billFromStreet, 14, 86);
    doc.text(`Phone: ${data.billFromPhone}`, 14, 91);
    doc.text(`Email: ${data.billFromEmail}`, 14, 96);
    if (data.billFromGst) doc.text(`PAN/GST: ${data.billFromGst}`, 14, 101);

    // Right Column: Client (Bill To)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(26, 35, 126);
    doc.text('CLIENT / BILL TO:', 110, 75);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(50, 50, 50);
    doc.text(data.billToCompany, 110, 81);
    
    // Handle wrapped address for client to prevent overlap
    const clientAddress = `${data.billToStreet}, ${data.billToCity}`;
    const splitClientAddress = doc.splitTextToSize(clientAddress, 86);
    doc.text(splitClientAddress, 110, 86);
    
    const clientAddressHeight = splitClientAddress.length * 4.5;
    
    doc.text(`Phone: ${data.billToPhone}`, 110, 86 + clientAddressHeight);
    doc.text(`Email: ${data.billToEmail}`, 110, 91 + clientAddressHeight);
    if (data.billToGst) doc.text(`GSTIN: ${data.billToGst}`, 110, 96 + clientAddressHeight);

    // End of Bill From/To section
    const billingSectionEndY = Math.max(
      data.billFromGst ? 101 : 96,
      96 + clientAddressHeight + (data.billToGst ? 5 : 0)
    );
    
    // 1st Divider Line (Clean line after Bill From and Bill To)
    const divider1Y = billingSectionEndY + 5;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.4);
    doc.line(14, divider1Y, 196, divider1Y);

    // 8. Bank Details Section (Placed below the first divider line)
    const bankDetailsStartY = divider1Y + 7;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(26, 35, 126);
    doc.text('BANK DETAILS (FOR REMITTANCE):', 14, bankDetailsStartY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(50, 50, 50);
    doc.text(`Bank Name: ${data.bankName}`, 14, bankDetailsStartY + 6);
    doc.text(`Account Name: ${data.accountName}`, 14, bankDetailsStartY + 11);
    doc.text(`Account No: ${data.accountNumber}`, 14, bankDetailsStartY + 16);
    doc.text(`IFSC Code: ${data.ifscCode}`, 14, bankDetailsStartY + 21);

    // 2nd Divider Line (Clean line after Bank Details)
    const divider2Y = bankDetailsStartY + 27;
    doc.line(14, divider2Y, 196, divider2Y);

    // 9. Table
    const tableHeaders = [['Description', 'Billing Rate', 'Quantity/Hours', 'Subtotal']];
    const tableRows = [
      [
        `Professional Services & Deliverables rendered by ${data.employeeName} (${data.employeeDesignation})`,
        `${currencySym} ${parseFloat(data.hourlyRate).toFixed(2)} / hr`,
        `${data.totalHours} hrs`,
        `${currencySym} ${parseFloat(data.contractTotal).toFixed(2)}`
      ]
    ];

    doc.autoTable({
      startY: divider2Y + 6,
      head: tableHeaders,
      body: tableRows,
      theme: 'grid',
      headStyles: { fillColor: [26, 35, 126], textColor: [255, 255, 255] },
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: {
        0: { cellWidth: 90 },
        1: { cellWidth: 30, halign: 'right' },
        2: { cellWidth: 35, halign: 'center' },
        3: { cellWidth: 27, halign: 'right' },
      }
    });

    let currentY = doc.lastAutoTable.finalY + 10;

    // 9. Financial Summary (Bank Details moved above the table)
    const summaryXLabel = 120;
    const summaryXVal = 196;

    const printRow = (label, val, y, isBold = false) => {
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      doc.setFontSize(isBold ? 9.5 : 8.5);
      doc.setTextColor(isBold ? 0 : 80, isBold ? 0 : 80, isBold ? 0 : 80);
      doc.text(label, summaryXLabel, y);
      doc.text(val, summaryXVal, y, { align: 'right' });
    };

    const sub = parseFloat(data.contractTotal) || 0;
    const gst = sub * ((parseFloat(data.gstRate) || 0) / 100);
    const tds = sub * ((parseFloat(data.tdsRate) || 0) / 100);
    const other = parseFloat(data.otherChargesValue) || 0;
    const tot = sub + gst - tds + other;

    printRow('Service Subtotal:', `${currencySym} ${sub.toFixed(2)}`, currentY);
    printRow(`GST (${data.gstRate}%):`, `(+) ${currencySym} ${gst.toFixed(2)}`, currentY + 5);
    printRow(`TDS Deductions (${data.tdsRate}%):`, `(-) ${currencySym} ${tds.toFixed(2)}`, currentY + 10);
    if (other > 0) {
      printRow(`${data.otherChargesLabel || 'Other'}:`, `(+) ${currencySym} ${other.toFixed(2)}`, currentY + 15);
    }
    
    doc.setFillColor(245, 245, 245);
    doc.rect(118, currentY + 19, 80, 8, 'F');
    printRow('Net Payable Amount:', `${currencySym} ${tot.toFixed(2)}`, currentY + 24, true);

    if (shouldSave) {
      doc.save(`proforma_${data.invoiceNumber}.pdf`);
    }
    return doc;
  };

  const clearForm = () => {
    setFormData((prev) => ({
      ...prev,
      billFromName: '',
      billFromStreet: '',
      billFromCity: '',
      billFromPhone: '',
      billFromEmail: '',
      billFromGst: '',
      bankName: '',
      accountName: '',
      accountNumber: '',
      ifscCode: '',
      selectedEmployee: '',
      employeeName: '',
      employeeDesignation: '',
      contractPeriod: '',
      hourlyRate: '',
      totalHours: 160,
      contractTotal: '0',
      otherChargesValue: '0',
    }));
    setErrors({});
  };

  const getDaysRemainingText = () => {
    let endDateStr = null;
    
    if (billingType === 'direct') {
      const employee = contractors.find(emp => emp.c_employee_id === formData.selectedEmployee);
      if (employee) {
        endDateStr = employee.isDirectContractor ? employee.contractEndDate : employee.Contract?.contractEndDate;
      }
    } else {
      const vendor = vendors.find(v => v.id === selectedVendorId);
      if (vendor) {
        endDateStr = vendor.contractEndDate;
      }
    }
    
    if (!endDateStr) return '';
    
    const endDate = new Date(endDateStr);
    const today = new Date();
    endDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `⚠️ Contract has expired (by ${Math.abs(diffDays)} days)`;
    } else if (diffDays === 0) {
      return `🔔 Contract expires today!`;
    } else {
      return `✅ ${diffDays} days left until contract end date`;
    }
  };

  const getDaysRemainingStyle = () => {
    let endDateStr = null;
    
    if (billingType === 'direct') {
      const employee = contractors.find(emp => emp.c_employee_id === formData.selectedEmployee);
      if (employee) {
        endDateStr = employee.isDirectContractor ? employee.contractEndDate : employee.Contract?.contractEndDate;
      }
    } else {
      const vendor = vendors.find(v => v.id === selectedVendorId);
      if (vendor) {
        endDateStr = vendor.contractEndDate;
      }
    }
    
    if (!endDateStr) return {};
    
    const endDate = new Date(endDateStr);
    const today = new Date();
    endDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { sx: { color: '#c62828', fontWeight: 'bold', fontSize: '0.85rem' } }; // Rich high-contrast Red
    } else if (diffDays === 0) {
      return { sx: { color: '#ef6c00', fontWeight: 'bold', fontSize: '0.85rem' } }; // Rich high-contrast Orange
    } else {
      return { sx: { color: '#1b5e20', fontWeight: 'bold', fontSize: '0.85rem' } }; // Rich high-contrast Green
    }
  };



  const renderTextField = (name, label, value, xs = 12, md = 6, required = false, type = 'text', InputProps = {}, customHelperText = '', FormHelperTextProps = {}) => (
    <Grid item xs={xs} md={md}>
      <TextField
        fullWidth
        size="small"
        label={label}
        name={name}
        type={type}
        value={value}
        onChange={handleChange}
        required={required}
        error={!!errors[name]}
        helperText={errors[name] || customHelperText}
        variant="outlined"
        InputProps={InputProps}
        FormHelperTextProps={FormHelperTextProps}
      />
    </Grid>
  );

  return (
    <Container maxWidth={false} sx={{ mt: 3, width: '100%' }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Title & Header Section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1a237e' }}>
            {isEditMode ? "Edit Pro Forma Invoice" : "Generate Pro Forma Invoice"}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {isEditMode 
              ? "Modify and regenerate service payouts and contract invoices for company contractors" 
              : "Generate and email service payouts and contract invoices for company contractors"}
          </Typography>
        </Box>
      </Box>

      {/* BILLING TYPE SELECTOR (DIRECT VS VENDOR) - MODERN PREMIUM TABS */}
      <Paper 
        elevation={0}
        variant="outlined"
        sx={{ 
          mb: 4, 
          borderColor: '#e2e8f0', 
          borderRadius: '16px', 
          overflow: 'hidden',
          bgcolor: '#f8fafc',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)'
        }}
      >
               <Tabs
          value={billingType}
          onChange={(e, val) => handleBillingTypeChange(val)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: '70px',
            '& .MuiTabs-indicator': {
              height: '4px',
              borderRadius: '4px 4px 0 0',
              backgroundColor: '#1a237e',
            },
          }}
        >

          <Tab
            value="direct"
            disabled={isEditMode}
            icon={<PersonIcon sx={{ fontSize: '22px', color: billingType === 'direct' ? '#1a237e' : '#64748b' }} />}
            iconPosition="start"
            label={
              <Box sx={{ textAlign: 'left', ml: 1.5 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ textTransform: 'none', color: billingType === 'direct' ? '#1a237e' : '#475569', lineHeight: 1.2 }}>
                  Direct Contractor Payout
                </Typography>
                <Typography variant="caption" sx={{ textTransform: 'none', color: '#64748b', display: { xs: 'none', sm: 'block' }, mt: 0.5 }}>
                  Individual consultant & service provider payouts
                </Typography>
              </Box>
            }
            sx={{
              py: 2,
              minHeight: '70px',
              transition: 'all 0.3s ease',
              borderBottom: '2px solid transparent',
              '&.Mui-selected': {
                bgcolor: '#fff',
              },
              '&:hover:not(.Mui-selected):not(.Mui-disabled)': {
                bgcolor: '#f1f5f9',
              },
            }}
          />
          <Tab
            value="vendor"
            disabled={isEditMode}
            icon={<BusinessIcon sx={{ fontSize: '22px', color: billingType === 'vendor' ? '#1a237e' : '#64748b' }} />}
            iconPosition="start"
            label={
              <Box sx={{ textAlign: 'left', ml: 1.5 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ textTransform: 'none', color: billingType === 'vendor' ? '#1a237e' : '#475569', lineHeight: 1.2 }}>
                  Vendor Agency Invoice
                </Typography>
                <Typography variant="caption" sx={{ textTransform: 'none', color: '#64748b', display: { xs: 'none', sm: 'block' }, mt: 0.5 }}>
                  Agency-firm consolidated corporate invoicing
                </Typography>
              </Box>
            }
            sx={{
              py: 2,
              minHeight: '70px',
              transition: 'all 0.3s ease',
              borderBottom: '2px solid transparent',
              '&.Mui-selected': {
                bgcolor: '#fff',
              },
              '&:hover:not(.Mui-selected):not(.Mui-disabled)': {
                bgcolor: '#f1f5f9',
              },
            }}
          />
        </Tabs>
      </Paper>

      <Paper elevation={4} sx={{ p: 4, borderRadius: 3, borderTop: '6px solid #1a237e' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#1a237e' }}>
              {isEditMode ? "Edit Service Invoice" : "Generate Service Invoice"}
            </Typography>
            <Stack direction="row" spacing={1.5}>
              {isEditMode ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<ClearIcon />}
                  onClick={() => navigate('/proforma-history')}
                >
                  Cancel Edit
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<ClearIcon />}
                  onClick={clearForm}
                >
                  Reset Form
                </Button>
              )}
            </Stack>
          </Box>
        <Divider sx={{ mb: 4 }} />

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            
            {/* SECTION 1: CONTRACT EMPLOYEE SELECTION & AUTO-FILL */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderColor: '#1a237e', borderWidth: 1.5, borderRadius: 2, bgcolor: '#fbfcfe' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ color: '#1a237e' }} display="flex" alignItems="center">
                    {billingType === 'direct' ? <PersonIcon sx={{ mr: 1 }} /> : <BusinessIcon sx={{ mr: 1 }} />}
                    {billingType === 'direct' ? ' Select Contract Employee to Auto-Fill Form' : ' Select Vendor Agency to Auto-Fill Form'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    {billingType === 'direct'
                      ? 'Choosing an employee will automatically load their profile address, phone, email, and bank details.'
                      : 'Choosing a vendor agency will automatically load their corporate details, corporate bank details, and compute consolidated hours/rates.'}
                  </Typography>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      {billingType === 'direct' ? (
                        <FormControl fullWidth size="small" required error={!!errors.selectedEmployee}>
                          <InputLabel>Select Contract Employee</InputLabel>
                          <Select
                            name="selectedEmployee"
                            value={formData.selectedEmployee}
                            onChange={handleEmployeeSelect}
                            label="Select Contract Employee"
                            startAdornment={
                              <InputAdornment position="start">
                                <PersonIcon />
                              </InputAdornment>
                            }
                          >
                            <MenuItem value="">
                              <em>-- Choose a consultant/contractor from active directory --</em>
                            </MenuItem>
                            {contractors.map((emp) => (
                              <MenuItem key={emp.c_employee_id} value={emp.c_employee_id}>
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">{emp.fullName}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {emp.designation} • ₹{emp.hourlyRate || 0}/hr • {emp.contractPeriod || 'N/A'}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.selectedEmployee && (
                            <Typography variant="caption" color="error">
                              {errors.selectedEmployee}
                            </Typography>
                          )}
                        </FormControl>
                      ) : (
                        <FormControl fullWidth size="small" required error={!!errors.selectedEmployee}>
                          <InputLabel>Select Vendor Agency</InputLabel>
                          <Select
                            value={selectedVendorId}
                            onChange={handleVendorSelect}
                            label="Select Vendor Agency"
                            startAdornment={
                              <InputAdornment position="start">
                                <BusinessIcon />
                              </InputAdornment>
                            }
                          >
                            <MenuItem value="">
                              <em>-- Choose a registered vendor/agency partner --</em>
                            </MenuItem>
                            {vendors.map((v) => (
                              <MenuItem key={v.id} value={v.id}>
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">{v.companyName}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {v.employmentType || 'Vendor Firm'} • {v.companyAddress || 'N/A'}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.selectedEmployee && (
                            <Typography variant="caption" color="error">
                              {errors.selectedEmployee}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    </Grid>

                    </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* SECTION 2: BILLING PARTIES */}
            {/* Bill From (Contractor Details - Auto-filled and editable if needed) */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold" color="primary" display="flex" alignItems="center">
                    <PersonIcon sx={{ mr: 1 }} /> {billingType === 'direct' ? 'Contractor / Employee (Receiving Payment)' : 'Vendor Agency (Receiving Payment)'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    {billingType === 'direct'
                      ? "These details are pulled automatically from the contractor's profile."
                      : "These details are pulled automatically from the vendor's corporate profile."}
                  </Typography>
                  <Grid container spacing={2}>
                    {renderTextField('billFromName', billingType === 'direct' ? 'Contractor Name' : 'Vendor Agency Name', formData.billFromName, 12, 12, true)}
                    {renderTextField('billFromStreet', 'Street Address / Residence', formData.billFromStreet, 12, 12, true)}
                    
                    {renderTextField('billFromPhone', 'Phone Number', formData.billFromPhone, 12, 6)}
                    {renderTextField('billFromEmail', 'Email Address', formData.billFromEmail, 12, 6, true)}
                    {renderTextField('billFromGst', billingType === 'direct' ? 'PAN / GSTIN (Contractor)' : 'Corporate PAN (Vendor)', formData.billFromGst, 12, 12)}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Bill To (Company Details - Auto-filled & Static) */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, bgcolor: '#fafafa' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold" color="primary" display="flex" alignItems="center">
                    <BusinessIcon sx={{ mr: 1 }} /> Company (Paying Party)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Your company details are auto-filled as the party making the payout.
                  </Typography>
                  <Grid container spacing={2}>
                    {renderTextField('billToCompany', 'Paying Company Name', formData.billToCompany, 12, 12, true, 'text', { readOnly: true })}
                    {renderTextField('billToStreet', 'Street Address', formData.billToStreet, 12, 12, true, 'text', { readOnly: true })}
                  
                    {renderTextField('billToPhone', 'Phone Number', formData.billToPhone, 12, 6, false, 'text', { readOnly: true })}
                    {renderTextField('billToEmail', 'Email Address', formData.billToEmail, 12, 6, true, 'text', { readOnly: true })}
                    {renderTextField('billToGst', 'GSTIN (Company)', formData.billToGst, 12, 12, false, 'text', { readOnly: true })}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* SECTION 3: INVOICE DETAILS & DURATION */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold" color="primary" display="flex" alignItems="center">
                    <DescriptionIcon sx={{ mr: 1 }} /> Billing Terms & Duration
                  </Typography>
                  <Grid container spacing={2}>
                    {renderTextField('invoiceNumber', 'Invoice Number', formData.invoiceNumber, 12, 3, true)}
                    {renderTextField('date', 'Invoice Date', formData.date, 12, 3, true, 'date')}
                    {renderTextField('dueDate', 'Due Date', formData.dueDate, 12, 3, true, 'date')}
                    
                    {formData.selectedEmployee && (
                      <>
                        {renderTextField('employeeDesignation', 'Designation / Title', formData.employeeDesignation, 12, 3, true)}
                        {renderTextField('contractPeriod', 'Contract Period', formData.contractPeriod, 12, 4, true, 'text', {}, getDaysRemainingText(), getDaysRemainingStyle())}
                        {renderTextField('hourlyRate', 'Hourly Rate (₹)', formData.hourlyRate, 12, 4, true, 'number')}
                        {renderTextField('totalHours', 'Total Approved Hours', formData.totalHours, 12, 4, true, 'number', {}, )}
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* SECTION 4: BANK REMITTANCE DETAILS */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold" color="primary" display="flex" alignItems="center">
                    <BankIcon sx={{ mr: 1 }} /> Contractor Bank Remittance Details
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    These details are auto-loaded from the contractor's payment profile.
                  </Typography>
                  <Grid container spacing={2}>
                    {renderTextField('bankName', 'Bank Name', formData.bankName, 12, 12, true)}
                    {renderTextField('accountName', 'Account Holder Name', formData.accountName, 12, 12, true)}
                    {renderTextField('accountNumber', 'Account Number', formData.accountNumber, 12, 12, true)}
                    {renderTextField('ifscCode', 'IFSC / SWIFT Code', formData.ifscCode, 12, 12, true)}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* SECTION 5: TAXES, TDS & FINANCIAL SUMMARY */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                    Financial Adjustments & Taxes
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    GST and TDS professional deductions are calculated automatically.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>GST Rate (%)</InputLabel>
                        <Select
                          name="gstRate"
                          value={formData.gstRate}
                          onChange={handleChange}
                          label="GST Rate (%)"
                        >
                          <MenuItem value="0">0% Exempt</MenuItem>
                          <MenuItem value="5">5% SGST + CGST</MenuItem>
                          <MenuItem value="12">12% Service Tax</MenuItem>
                          <MenuItem value="18">18% Professional Services GST</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>TDS Deduction (%)</InputLabel>
                        <Select
                          name="tdsRate"
                          value={formData.tdsRate}
                          onChange={handleChange}
                          label="TDS Deduction (%)"
                        >
                          <MenuItem value="0">0% No TDS</MenuItem>
                          <MenuItem value="2">2% Technical Services (194J)</MenuItem>
                          <MenuItem value="10">10% Professional Services (194J)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {renderTextField('otherChargesLabel', 'Other Charges Description', formData.otherChargesLabel, 12, 6)}
                    {renderTextField('otherChargesValue', 'Other Amount (₹)', formData.otherChargesValue, 12, 6, false, 'number')}
                  </Grid>

                  {/* Calculations breakdown */}
                  <Box mt={3} p={2} sx={{ bgcolor: '#f1f5f9', borderRadius: 1.5 }}>
                    <Stack spacing={1}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Service Subtotal:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          ₹{calculateSubtotal().toFixed(2)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="success.main">GST Invoiced (+):</Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          ₹{calculateGst().toFixed(2)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="error.main">TDS Professional Deduct (-):</Typography>
                        <Typography variant="body2" fontWeight="bold" color="error.main">
                          ₹{calculateTds().toFixed(2)}
                        </Typography>
                      </Box>
                      {parseFloat(formData.otherChargesValue) > 0 && (
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">{formData.otherChargesLabel || 'Other'} (+):</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            ₹{parseFloat(formData.otherChargesValue).toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" fontWeight="bold" color="#1a237e">Net Payable:</Typography>
                        <Typography variant="h5" fontWeight="bold" color="#1a237e">
                          {formData.currency === 'INR' ? '₹' : '$'} {calculateTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

          </Grid>

          <Box mt={4} display="flex" justifyContent="center">
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                bgcolor: '#1a237e',
                px: 6,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(26, 35, 126, 0.2)',
                '&:hover': {
                  bgcolor: '#0d47a1',
                  boxShadow: '0 6px 16px rgba(26, 35, 126, 0.3)',
                }
              }}
            >
              {loading ? (
                <LinearProgress sx={{ width: 150 }} />
              ) : isEditMode ? (
                'Update'
              ) : (
                'Generate'
              )}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default ProFormaInvoice;